import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ModalContext } from "components/Modals/ModalContext"
import { bulkAddProgramTableOverrides, bulkRemoveProgramTableOverrides, clearDraggedSlots } from 'redux/slices/trainingChart'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import moment from 'moment'

import OverrideInputModalRouteView from 'components/Modals/Components/BulkEditorOverrideModal/OverrideInputModalRouteView'
import BulkEditorExerciseListModalRouteView from 'components/Modals/Components/BulkEditorOverrideModal/BulkEditorExerciseListModalRouteView'
import BulkEditorExerciseInstructionsModalRouteView from 'components/Modals/Components/BulkEditorOverrideModal/BulkEditorExerciseInstructionsModalRouteView'
import BulkEditorExerciseCreateModalRouteView from 'components/Modals/Components/BulkEditorOverrideModal/BulkEditorExerciseCreateModalRouteView'

export default function BulkEditorOverrideModal() {

    const dispatch = useDispatch()

    const { modalData, HideModal } = useContext(ModalContext)
    const { exercises } = useGetExercises()

    const coachId = useSelector(state => state.user.id)
    const profileType = useSelector(state => state.trainingChart.profileType)
    const groupId = useSelector(state => state.trainingChart.groupId)
    const athleteId = useSelector(state => state.trainingChart.athleteId)
    const latestProgramSnapshot = useSelector(state => state.trainingChart.latestProgramSnapshot)

    const [modalRoute, setModalRoute] = useState('override-input')
    const [setsOverrideValue, setSetsOverrideValue] = useState(null)
    const [exerciseOverrideValue, setExerciseOverrideValue] = useState(null)  
    const [repsOverrideValue, setRepsOverrideValue] = useState(null)
    const [showWeightRecommendationOverrideInput, setShowWeightRecommendationOverrideInput] = useState(false)
    const [weightRecommendationOverrideValue, setWeightRecommendationOverrideValue] = useState(null)
    const [weightRecommendationPreview, setWeightRecommendationPreview] = useState(null)
    const [weightRecommendationPreviewInputs, setWeightRecommendationPreviewInputs] = useState(null)

    const InitializeWeightRecommendationComponent = async () => {

        const visibility = GetWeightRecommendationComponentVisibility()

        setShowWeightRecommendationOverrideInput(visibility)

        if (visibility === false) {
            setShowWeightRecommendationOverrideInput(false)
            setWeightRecommendationOverrideValue(null)
            setWeightRecommendationPreview(null)
            setWeightRecommendationPreviewInputs(null)
            return
        }

        const taperData = GetWeightRecommendationTaperData()
        const exercise = GetWeightRecommendationPreviewExercise()
        const reps = GetWeightRecommendationPreviewReps()

        setWeightRecommendationPreview(null)

        const previewWeightRecommendation = await LoadPreviewWeightRecommendation(taperData, exercise, reps)

        setWeightRecommendationPreview(previewWeightRecommendation)

        setWeightRecommendationPreviewInputs({
            taperData: taperData,
            exercise: exercise,
            reps: reps
        })
    }

    const GetWeightRecommendationComponentVisibility = () => {

        if (
            exerciseOverrideValue !== null &&
            exerciseOverrideValue.exerciseType === 'recommendation'
        ) {
            return true
        }

        // Safety check in dragged slots

        if (!modalData.draggedSlots || modalData.draggedSlots.length === 0) {
            return false
        }

        // Grab first dragged exercise

        const firstSlotExerciseId = modalData.draggedSlots[0].exercise?.id

        if (!firstSlotExerciseId) {
            return false
        }

        // Get full exercise data

        const firstExercise = exercises.find(e => e.id === firstSlotExerciseId) || null

        if (!firstExercise || firstExercise.exerciseType !== 'recommendation') {
            return false
        }

        // Verify they are all the same

        const allSameExercise = modalData.draggedSlots.every(slot =>
            slot.exercise &&
            slot.exercise.id === firstSlotExerciseId
        )

        return allSameExercise

    }

    const GetWeightRecommendationTaperData = () => {

        var taperData = null

        const firstDraggedSlot = modalData.draggedSlots[0]
        const weekOfTheYearKey = firstDraggedSlot.weekOfTheYearKey
        const workoutTemplateKey = firstDraggedSlot.workoutTemplateKey
        const phaseIndex = firstDraggedSlot.phaseIndex

        // console.log(firstDraggedSlot)
        // console.log(programTableColumn)

        const programTableColumn = latestProgramSnapshot.programTable.find(week => week.weekOfTheYearKey === weekOfTheYearKey)

        // Use phase-level taper data if available, otherwise fall back to program-level

        const phaseVolumeData = programTableColumn.volumeData.setsAndRepsData[workoutTemplateKey][phaseIndex]

        const hasPhaseTaperData = (
            phaseVolumeData 
            && phaseVolumeData.taperData 
            && phaseVolumeData.taperData.source === 'phase-load-adjustment-override' 
            && phaseVolumeData.taperData.overrideApplied
        )

        taperData = hasPhaseTaperData ? phaseVolumeData.taperData : programTableColumn.volumeData.taperData

        // console.log(taperData)
        
        return taperData

    }

    const GetWeightRecommendationPreviewExercise = () => {

        if (
            exerciseOverrideValue !== null
            && exerciseOverrideValue.exerciseType === 'recommendation'
        ) {
            return exerciseOverrideValue
        }

        const firstDraggedSlot = modalData.draggedSlots[0]

        return firstDraggedSlot.exercise
        
    }

    const GetWeightRecommendationPreviewReps = () => {

        if (repsOverrideValue !== null) {
            return repsOverrideValue
        }

        const firstDraggedSlot = modalData.draggedSlots[0]

        const firstDraggedSlotSlotIndex = firstDraggedSlot.slotIndex
        const firstDraggedSlotExerciseLateral = firstDraggedSlot.exercise.lateral

        return firstDraggedSlot.volumeData.reps[firstDraggedSlotSlotIndex][firstDraggedSlotExerciseLateral]
        
    }

    const LoadPreviewWeightRecommendation = async (taperData, exercise, reps) => {

        try {
            const firstDraggedSlot = modalData.draggedSlots[0]
            const weekOfTheYearKey = firstDraggedSlot.weekOfTheYearKey
            const workoutTemplateKey = firstDraggedSlot.workoutTemplateKey
            const phaseIndex = firstDraggedSlot.phaseIndex
            const slotIndex = firstDraggedSlot.slotIndex

            var jsonBody = null

            if (profileType === 'group') {
                jsonBody = {
                    groupId: groupId,
                    weekOfTheYearKey: weekOfTheYearKey,
                    workoutTemplateKey: workoutTemplateKey,
                    phaseIndex: phaseIndex,
                    slotIndex: slotIndex,
                    exerciseId: exercise.id,
                    reps: reps,
                    taper: taperData.useTaper ? 1 : 0,
                    taperDampener: taperData.taperDampener
                }
            }

            if (profileType === 'individual') {
                jsonBody = {
                    userId: athleteId,
                    weekOfTheYearKey: weekOfTheYearKey,
                    workoutTemplateKey: workoutTemplateKey,
                    phaseIndex: phaseIndex,
                    slotIndex: slotIndex,
                    exerciseId: exercise.id,
                    reps: reps,
                    taper: taperData.useTaper ? 1 : 0,
                    taperDampener: taperData.taperDampener
                }
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('PROGRAM', 'GET', '/weight_recommendation', jsonBody)

            // console.log(result.data.data)

            return result.data.data

        } catch (error) {
            console.error(error)
            return 10
        }

    }

    const ClearExistingOverrides = () => {

        var overrideIndicesToRemove = []

        if (setsOverrideValue !== null) {

            // console.log(setsOverrideValue)

            for (const draggedSlot of modalData.draggedSlots) {

                // one-off-sets-override

                const cachedSetsOverride = modalData.SearchForPhaseOverrideInCache(
                    draggedSlot.weekOfTheYearKey,
                    draggedSlot.workoutTemplateKey, 
                    'one-off-sets-override', 
                    draggedSlot.phaseIndex
                )

                if (cachedSetsOverride !== null) {
                    overrideIndicesToRemove.push(cachedSetsOverride.index)
                }

                // one-off-exercise-swap

                const cachedExerciseOverride = modalData.SearchForSlotOverrideInCache(
                    draggedSlot.weekOfTheYearKey,
                    draggedSlot.workoutTemplateKey,
                    'one-off-exercise-swap',
                    draggedSlot.phaseIndex,
                    draggedSlot.slotIndex
                )

                if (cachedExerciseOverride !== null) {
                    overrideIndicesToRemove.push(cachedExerciseOverride.index)
                }

                // one-off-reps-override

                const cachedRepsOverride = modalData.SearchForSlotOverrideInCache(
                    draggedSlot.weekOfTheYearKey,
                    draggedSlot.workoutTemplateKey,
                    'one-off-reps-override',
                    draggedSlot.phaseIndex,
                    draggedSlot.slotIndex
                )

                if (cachedRepsOverride !== null) {
                    overrideIndicesToRemove.push(cachedRepsOverride.index)
                }

                // one-off-weight-recommendation-override

                const cachedWeightRecommendationOverride = modalData.SearchForSlotOverrideInCache(
                    draggedSlot.weekOfTheYearKey,
                    draggedSlot.workoutTemplateKey,
                    'one-off-weight-recommendation-override',
                    draggedSlot.phaseIndex,
                    draggedSlot.slotIndex
                )

                if (cachedWeightRecommendationOverride !== null) {
                    overrideIndicesToRemove.push(cachedWeightRecommendationOverride.index)
                }

            }

        }

        if (overrideIndicesToRemove.length > 0) {
            dispatch(bulkRemoveProgramTableOverrides(overrideIndicesToRemove))
        }

    }

    const CreateOverrides = () => {

        var newOverrides = []

        // one-off-sets-override

        if (setsOverrideValue !== null) {

            // console.log(setsOverrideValue)

            for (const draggedSlot of modalData.draggedSlots) {

                var newOverride = {
                    overrideKey: 'one-off-sets-override',
                    kinesisUserGroupId: groupId,
                    userId: athleteId,
                    source: {
                        coachId: coachId
                    },
                    target: {
                        weekOfTheYearKey: draggedSlot.weekOfTheYearKey,
                        workoutTemplateKey: draggedSlot.workoutTemplateKey,
                        phaseIndex: parseInt(draggedSlot.phaseIndex),
                        sets: parseInt(setsOverrideValue)
                    },
                    createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                    archived: 0
                }

                newOverrides.push(newOverride)

            }

        }

        // one-off-exercise-swap

        if (exerciseOverrideValue !== null) {

            for (const draggedSlot of modalData.draggedSlots) {

                var newOverride = {
                    overrideKey: 'one-off-exercise-swap',
                    kinesisUserGroupId: groupId,
                    userId: athleteId,
                    source: {
                        coachId: coachId
                    },
                    target: {
                        weekOfTheYearKey: draggedSlot.weekOfTheYearKey,
                        workoutTemplateKey: draggedSlot.workoutTemplateKey,
                        phaseIndex: parseInt(draggedSlot.phaseIndex),
                        slotIndex: parseInt(draggedSlot.slotIndex),
                        exerciseId: exerciseOverrideValue.id,
                        exerciseName: exerciseOverrideValue.name,
                        lateral: exerciseOverrideValue.lateral,
                        progressionLevel: exerciseOverrideValue.progressionLevel,
                    },
                    createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                    archived: 0
                }

                newOverrides.push(newOverride)

            }

        }

        // one-off-reps-override

        if (repsOverrideValue !== null) {

            for (const draggedSlot of modalData.draggedSlots) {

                var newOverride = {
                    overrideKey: 'one-off-reps-override',
                    kinesisUserGroupId: groupId,
                    userId: athleteId,
                    source: {
                        coachId: coachId
                    },
                    target: {
                        weekOfTheYearKey: draggedSlot.weekOfTheYearKey,
                        workoutTemplateKey: draggedSlot.workoutTemplateKey,
                        phaseIndex: parseInt(draggedSlot.phaseIndex),
                        slotIndex: parseInt(draggedSlot.slotIndex),
                        reps: parseInt(repsOverrideValue)
                    },
                    createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                    archived: 0
                }

                newOverrides.push(newOverride)

            }

        }

        // one-off-weight-recommendation-override

        if (weightRecommendationOverrideValue !== null) {

            const exercise = GetWeightRecommendationPreviewExercise()

            for (const draggedSlot of modalData.draggedSlots) {

                var newOverride = {
                    overrideKey: 'one-off-weight-recommendation-override',
                    kinesisUserGroupId: groupId,
                    userId: athleteId,
                    source: {
                        coachId: coachId
                    },
                    target: {
                        weekOfTheYearKey: draggedSlot.weekOfTheYearKey,
                        workoutTemplateKey: draggedSlot.workoutTemplateKey,
                        phaseIndex: parseInt(draggedSlot.phaseIndex),
                        slotIndex: parseInt(draggedSlot.slotIndex),
                        exerciseId: parseInt(exercise.id),
                        weightRecommendation: parseInt(weightRecommendationOverrideValue)
                    },
                    createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                    archived: 0
                }

                newOverrides.push(newOverride)

            }

        }

        return newOverrides

    }

    const OnCreateButton = () => {

        ClearExistingOverrides()

        const newOverrides = CreateOverrides()

        dispatch(bulkAddProgramTableOverrides(newOverrides))

        dispatch(clearDraggedSlots())

        HideModal()

    }

    useEffect(() => { 
        InitializeWeightRecommendationComponent() 
    }, [exerciseOverrideValue])

    if (modalData.athleteId === null || exercises.length === 0) {
        return (
            <div className='flex flex-col overflow-y-auto'>
                <div className='space-y-4 p-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </div>
        )
    }

    if (modalData.workoutTemplateKey === 'mobility-a' || modalData.workoutTemplateKey === 'mobility-b') {
        return (
            <div>
                <p className='text-center'>Mobility workouts do not have override support yet</p>
            </div>
        )
    }

    return (
        <>
            {modalRoute === 'override-input' && (
                <OverrideInputModalRouteView 
                    exercises={exercises} 
                    modalData={modalData}
                    setsOverrideValue={setsOverrideValue}
                    setSetsOverrideValue={setSetsOverrideValue}
                    exerciseOverrideValue={exerciseOverrideValue}
                    repsOverrideValue={repsOverrideValue}
                    setRepsOverrideValue={setRepsOverrideValue}
                    showWeightRecommendationOverrideInput={showWeightRecommendationOverrideInput}
                    weightRecommendationOverrideValue={weightRecommendationOverrideValue}
                    setWeightRecommendationOverrideValue={setWeightRecommendationOverrideValue}
                    InitializeWeightRecommendationComponent={InitializeWeightRecommendationComponent}
                    weightRecommendationPreview={weightRecommendationPreview}
                    weightRecommendationPreviewInputs={weightRecommendationPreviewInputs}
                    setModalRoute={setModalRoute} 
                    OnCreateButton={OnCreateButton}
                />
            )}

            {modalRoute === 'exercise-list' && (
                <BulkEditorExerciseListModalRouteView 
                    exercises={exercises}
                    setExerciseOverrideValue={setExerciseOverrideValue}
                    setModalRoute={setModalRoute}
                />
            )}

            {modalRoute === 'exercise-instructions' && (
                <BulkEditorExerciseInstructionsModalRouteView 
                    exerciseOverrideValue={exerciseOverrideValue}
                    setExerciseOverrideValue={setExerciseOverrideValue}
                    setModalRoute={setModalRoute}
                />
            )}

            {modalRoute === 'exercise-create' && (
                <BulkEditorExerciseCreateModalRouteView 
                    setExerciseOverrideValue={setExerciseOverrideValue}
                    setModalRoute={setModalRoute}
                />
            )}
        </>
    )
}